import React, { useState } from 'react';
import styles from './TelemetryCard.module.css';
import classnames from 'classnames';
import {
  Alert, Detector, Device, DeviceSystemMode, DeviceType, isLocationFullAccess, Measure,
  RealTimeDeviceData, SystemMode, systemModeToString, Unit, UnitSystem, User, ValveState
} from 'model';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Toggle from 'react-toggle';
import "react-toggle/style.css";
import ContentLoader from "react-content-loader";
import { ConfirmationPopupFunction } from 'effects';
import { TelemetryCardHeader } from './TelemetryCardHeader';
import { FloMenuButton } from "../FloMenuButton";
import { analytics } from 'utils/Analytics';


enum Icon {
  TEMPERATURE,
  PRESSURE,
  HUMIDITY,
  FLOW,
  BATTERY_FULL,
  BATTERY_MED,
  BATTERY_EMPTY
}

type TelemetryWidgetProps = {
  title: string;
  measure: Measure;
  icon: Icon;
}

const TelemetryWidget: React.FC<TelemetryWidgetProps> = React.memo(({measure, title, icon}) => (
  <div className={styles.measureBox}>
    <div
      className = {classnames(
        styles.measureIcon,
        { [styles.temperature]: icon === Icon.TEMPERATURE },
        { [styles.pressure]: icon === Icon.PRESSURE },
        { [styles.flow]: icon === Icon.FLOW },
        { [styles.humidity]: icon === Icon.HUMIDITY },
        { [styles.battery100]: icon === Icon.BATTERY_FULL },
        { [styles.battery50]: icon === Icon.BATTERY_MED },
        { [styles.battery10]: icon === Icon.BATTERY_EMPTY },
      )}>
    </div>
    <div><span className={styles.value}>{measure.value}</span><span className={styles.shortUnit}>{measure.short}</span></div>
    <div className={styles.title}>{title}</div>
  </div>
));

type SystemModeWidgetProps = {
  systemMode: DeviceSystemMode
}

const SystemModeWidget: React.FC<SystemModeWidgetProps> = ({systemMode}) => {
  const { t } = useTranslation();
  const displayMode = systemMode.isLocked ? t('Learning') : systemModeToString(systemMode.lastKnown || SystemMode.HOME);
  return <span>{displayMode}</span>
}

type TelemetryCardProps = {
  currentDevice?:                 Device,
  currentUnit?:                   Unit,
  realtime?:                      RealTimeDeviceData,
  loggedUser?:                    User,
  updatedTelemetryText:           string,
  isLoadingUnit:                  boolean,
  changeValveState:               (deviceId: string, target: ValveState) => void,
  deleteDevice:                   (deviceId: string) => void,
  updateDevice:                   (deviceId: string, device: Partial<Device>) => void,
  openConfirmationPopup:          ConfirmationPopupFunction,
  openTextConfirmationPopup:      ConfirmationPopupFunction,
  handleRenameDevicePopup:        ConfirmationPopupFunction,
  openSetThresholds:              () => void,
  openAutoShutoff:                 () => void,
  openAlarmSettings:              () => void,
  pendingAlertsForCurrentDevice:  Alert[],
  resolveAlert:                   (alert: Alert, snoozeSeconds?: number) => void,
  runHealthTest:                  (deviceId: string) => void,
  openDeveloperMenu:              () => void,
}

const emptyMeasure = { value: 0, unit: UnitSystem.IMPERIAL, short: '', long: '' };

export const TelemetryCard: React.FC<TelemetryCardProps> =  React.memo(({
  currentDevice,
  currentUnit,
  realtime,
  loggedUser,
  isLoadingUnit,
  changeValveState,
  deleteDevice,
  updateDevice,
  openConfirmationPopup,
  openTextConfirmationPopup,
  handleRenameDevicePopup,
  pendingAlertsForCurrentDevice,
  resolveAlert,
  runHealthTest,
  ...rest
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Row className={styles.root}>
        { isLoadingUnit ?
          <Col xs="12">
            <TelemetryCardSkeleton />
          </Col> : currentDevice ?
          <Col xs="12">
            <TelemetryCardHeader {...rest}
              currentDevice={currentDevice}
              currentUnit={currentUnit}
              loggedUser={loggedUser}
              openTextConfirmationPopup={openTextConfirmationPopup}
              handleRenameDevicePopup={handleRenameDevicePopup}
              deleteDevice={deleteDevice}
              updateDevice={updateDevice}
              openConfirmationPopup={openConfirmationPopup}
              runHealthTest={runHealthTest}
            />
            { currentDevice.type === DeviceType.SHUTOFF ?
              <ShutoffDeviceCard
                realtime                = {realtime}
                currentDevice           = {currentDevice}
                openConfirmationPopup   = {openConfirmationPopup}
                changeValveState        = {changeValveState}
                currentUnit             = {currentUnit}
                loggedUser              = {loggedUser}
              />
            :
              <DetectorDeviceCard
                currentDevice                 = {currentDevice}
                openConfirmationPopup         = {openConfirmationPopup}
                pendingAlertsForCurrentDevice = {pendingAlertsForCurrentDevice}
                resolveAlert                  = {resolveAlert}
              />
            }
          </Col>
          :
          <Col xs="12">
            <Row className={styles.noDataContainer}><Col xs="12">{t('No Devices')}</Col></Row>
          </Col>
        }
      </Row>
    </>
  );
});

interface ShutoffDeviceCardProps {
  realtime?:              RealTimeDeviceData;
  currentDevice:          Device;
  openConfirmationPopup:  ConfirmationPopupFunction;
  changeValveState:       (deviceId: string, target: ValveState) => void;
  currentUnit?:           Unit;
  loggedUser?:            User;
}

const ShutoffDeviceCard: React.FC<ShutoffDeviceCardProps> = ({realtime, currentDevice, openConfirmationPopup, changeValveState, currentUnit, loggedUser}) => {

  const { t } = useTranslation();
  const [ valveTarget, setValveTarget ] = useState(ValveState.UNKNOWN);

  const telemetry =   realtime ? realtime.telemetry :               currentDevice?.telemetry;
  const valveState =  realtime ? realtime.valve?.lastKnown :        currentDevice?.valve.lastKnown;

  const handleValveToggle = () => {
    const target = valveState === ValveState.CLOSED ? ValveState.OPEN : ValveState.CLOSED;

    openConfirmationPopup(
      t(`valve-toggle-confirmation-title-water-${target === ValveState.OPEN ? 'on': 'off'}`),
      t(`valve-toggle-confirmation-description-water-${target === ValveState.OPEN ? 'on': 'off'}`),
      t('valve-toggle-action'),
      () => {
        changeValveState(currentDevice?.id || '', target)
        setValveTarget(target);
        analytics.trackValveAction(loggedUser?.id || '')
      }
    )
  };

  return (
    <>
      <Row className={styles.telemetryWrapper}>
        <Col xs={{span: 4, offset: 0}} className={styles.smallerColPadding}>
          <TelemetryWidget title={t('Flow Rate')} measure={telemetry?.flow || emptyMeasure} icon={Icon.FLOW}/>
        </Col>
        <Col xs={{span: 4, offset: 0}} className={styles.smallerColPadding}>
          <TelemetryWidget title={t('Pressure')} measure={telemetry?.pressure || emptyMeasure} icon={Icon.PRESSURE}/>
        </Col>
        <Col xs={{span: 4, offset: 0}} className={styles.smallerColPadding}>
          <TelemetryWidget title={t('Temperature')} measure={telemetry?.temp || emptyMeasure} icon={Icon.TEMPERATURE}/>
        </Col>
      </Row>
      <Row className={styles.controls}>
        <Col xs="9" className={styles.smallerColPadding}>
          <Toggle
            id='valve-open'
            icons={false}
            disabled={(valveTarget !== ValveState.UNKNOWN && valveTarget !== valveState) || !isLocationFullAccess(currentUnit) || !currentDevice.isConnected}
            checked={valveState === ValveState.OPEN}
            className={styles.toggle}
            onChange={handleValveToggle}/>
          <label className={styles.toggleLabel}
                 htmlFor='valve-open'>{`${t('Valve')} ${valveState === ValveState.OPEN ? t('Open') : t('Closed')}`}</label>
        </Col>
        <Col xs="3" className={styles.systemMode}>
          <SystemModeWidget systemMode={currentDevice.systemMode}/>
        </Col>
      </Row>
    </>
  );
}

interface DetectorDeviceCardProps {
  currentDevice:                  Device;
  openConfirmationPopup:          ConfirmationPopupFunction;
  pendingAlertsForCurrentDevice:  Alert[],
  resolveAlert:                   (alert: Alert, snoozeSeconds?: number) => void,
}

const DetectorDeviceCard: React.FC<DetectorDeviceCardProps> = ({currentDevice, openConfirmationPopup, resolveAlert, pendingAlertsForCurrentDevice}) => {
  const { t } = useTranslation();

  const handleSnooze = (minutes: number) => {
    const snoozeFor = (minutes < 60) ? `${minutes} ${t('minutes')}` : `${minutes/60} ${t('hours')}`;
    openConfirmationPopup(
      t('detector-snooze-title', {snoozeFor}),
      t('detector-snooze-description', {snoozeFor}),
      t('detector-snooze-action'),
      () => {
        const alert = pendingAlertsForCurrentDevice.find(a => a.alarm.id === 100);
        if (alert) {
          resolveAlert(alert, minutes * 60);
        }
      }
    );
  }

  const getBatteryIcon = (battery: Measure): Icon => {
    if (battery.value > 50) {
      return Icon.BATTERY_FULL
    } else if (battery.value > 13 && battery.value <= 50) {
      return Icon.BATTERY_MED;
    } else {
      return Icon.BATTERY_EMPTY;
    }
  }

  return (
    <>
      <Row className={styles.telemetryWrapper}>
        <Col xs={{span: 4, offset: 0}} className={styles.smallerColPadding}><TelemetryWidget title={t('Humidity')} measure={currentDevice?.telemetry.humidity || emptyMeasure} icon={Icon.HUMIDITY} /></Col>
        <Col xs={{span: 4, offset: 0}} className={styles.smallerColPadding}><TelemetryWidget title={t('Battery')} measure={currentDevice?.telemetry.battery || emptyMeasure} icon={getBatteryIcon(currentDevice.telemetry.battery)} /></Col>
        <Col xs={{span: 4, offset: 0}} className={styles.smallerColPadding}><TelemetryWidget title={t('Temperature')} measure={currentDevice?.telemetry.temp || emptyMeasure} icon={Icon.TEMPERATURE} /></Col>
      </Row>
      { (currentDevice as Detector).isDetectingWater &&
        <Row className={styles.waterDetected}>
          <Col xs="6" className="">
            <span className={styles.waterDetectedText}>{`${t('water-detected')}!`}</span>
          </Col>
          <Col xs="6" className="pr-0">
            <FloMenuButton
                className   = {styles.snoozeButton}
                text        = {t('Snooze')}
                name        = "snooze-btn"
                menuItems   = {[
                  { id: 'snooze-5', onClick: () => handleSnooze(5) ,title: t('for-x-minutes', {time: '5'}) },
                  { id: 'snooze-30', onClick: () => handleSnooze(30) ,title: t('for-x-minutes', {time: '30'}) },
                  { id: 'snooze-360', onClick: () => handleSnooze(360) ,title: t('for-x-hours', {time: '6'}) },
                ]}
            />
          </Col>
        </Row>
      }
    </>
  );
}

const TelemetryCardSkeleton: React.FC<{}> = () => (
  <ContentLoader
    height={280}
    width={'100%'}
    speed={3}
  >
    <rect x="0" y="10" rx="3" ry="3" width="100" height="30" />
    <rect x="0" y="45" rx="3" ry="3" width="300" height="10" />


    <rect x="0" y="80" rx="8" ry="8" width="120" height="150" />
    <rect x="130" y="80" rx="8" ry="8" width="120" height="150" />
    <rect x="260" y="80" rx="8" ry="8" width="120" height="150" />

    <rect x="0" y="250" rx="3" ry="3" width="100" height="20" />
  </ContentLoader>
)

export default TelemetryCard;
